import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { motion } from 'framer-motion';
import { Loader2, Zap, Cog, Settings, Wrench, Cpu, MoreHorizontal, FolderOpen } from 'lucide-react';
import { apiEndpoints, apiRequest, responsiveApiAssetUrl } from '@/config/api';
import { Product, Category, ApiResponse } from '@/types/api';
import ScrollToTop from '@/hooks/ScrollToTop';
import SeoHead, { companySchema, SITE_URL } from '@/components/SeoHead';
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Zap,
  Cog,
  Settings,
  Wrench,
  Cpu,
  MoreHorizontal,
  Folder: FolderOpen,
  FolderOpen,
};

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFromUrl = Number(searchParams.get('category')) || null;
  const [selectedCategory, setSelectedCategory] = useState<number | null>(categoryFromUrl);

  useEffect(() => {
    setSelectedCategory(categoryFromUrl);
  }, [categoryFromUrl]);

  const changeCategory = (categoryId: number | null) => {
    setSelectedCategory(categoryId);
    setSearchParams(categoryId ? { category: String(categoryId) } : {}, { replace: true });
  };

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiRequest<ApiResponse<Category[]>>(apiEndpoints.categories),
  });

  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['products', selectedCategory],
    queryFn: () => apiRequest<ApiResponse<Product[]>>(
      `${apiEndpoints.products}${selectedCategory ? `?category=${selectedCategory}` : ''}`
    ),
  });

  const categories = categoriesData?.data || [];
  const products = productsData?.data || [];

  const isLoading = categoriesLoading || productsLoading;
  const productListSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'محصولات کیا الکتروموتور',
    description: 'فهرست الکتروموتورهای صنعتی، هودی، کولری، آکسیال، تانژانت، پرتابل و براشلس کیا الکتروموتور.',
    url: `${SITE_URL}/products`,
    about: { '@id': `${SITE_URL}/#organization` },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: products.length,
      itemListElement: products.map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${SITE_URL}/product/${product.slug}`,
        name: product.title,
      })),
    },
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <SeoHead
        title="محصولات کیا الکتروموتور | الکتروموتور صنعتی و فن"
        description="مشاهده مجموعه محصولات کیا الکتروموتور شامل الکتروموتورهای صنعتی، هودی، کولری، آکسیال، تانژانت، پرتابل و براشلس به همراه مشخصات و تصاویر."
        canonicalPath="/products"
        keywords={['محصولات کیا الکتروموتور', 'الکتروموتور صنعتی', 'موتور هود', 'موتور کولری', 'فن آکسیال', 'BLDC']}
        structuredData={[{ '@context': 'https://schema.org', ...companySchema }, productListSchema]}
      />
      <ScrollToTop />

      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-background to-muted">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                محصولات ما
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                مجموعه کاملی از الکترو موتورهای الکتریکی با کیفیت برای انواع کاربردهای صنعتی و تجاری
              </p>
            </motion.div>
          </div>
        </section>

        {/* Categories Filter */}
        {categories.length > 0 && (
          <section className="py-8 bg-muted/50 border-b border-border">
            <div className="container mx-auto px-4">
              <div className="flex snap-x snap-mandatory justify-start gap-3 overflow-x-auto px-1 pb-3 scrollbar-thin md:flex-wrap md:justify-center md:overflow-visible md:pb-0">
                <button
                  onClick={() => changeCategory(null)}
                  className={`flex h-12 min-w-[170px] shrink-0 snap-start items-center justify-center px-5 py-3 rounded-xl font-medium transition-all duration-300 ${selectedCategory === null
                    ? 'bg-copper text-white shadow-lg shadow-copper/30'
                    : 'bg-card text-foreground hover:bg-copper/10 border border-border'
                    }`}
                >
                  همه محصولات
                </button>
                {categories.map((category) => {
                  const IconComponent = iconMap[category.icon] || FolderOpen;
                  return (
                    <button
                      key={category.id}
                      onClick={() => changeCategory(category.id)}
                      className={`flex h-12 min-w-[170px] shrink-0 snap-start items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium transition-all duration-300 ${selectedCategory === category.id
                        ? 'bg-copper text-white shadow-lg shadow-copper/30'
                        : 'bg-card text-foreground hover:bg-copper/10 border border-border'
                        }`}
                    >
                      <IconComponent className="w-4 h-4" />
                      {category.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Products Grid */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-12 h-12 animate-spin text-copper" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg">محصولی یافت نشد</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((product, index) => {
                  const IconComponent = iconMap[product.icon] || Zap;
                  return (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: index * 0.05 }}
                      viewport={{ once: true }}
                    >
                      <Link
                        to={`/product/${product.slug}`}
                        className="group block bg-card rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-border hover:border-copper/30"
                      >
                        {/* Product Image */}
                        <div className="relative h-48 bg-muted overflow-hidden">
                          {product.main_image || (product.images && product.images.length > 0) ? (
                            <img
                              src={
                                responsiveApiAssetUrl(product.main_image || product.images?.[0]?.image_url, 480) ||
                                '/images/electric-motor.webp'
                              }
                              srcSet={[320, 640].map((width) => responsiveApiAssetUrl(product.main_image || product.images?.[0]?.image_url, width)).filter(Boolean).map((url, index) => `${url} ${index === 0 ? 320 : 640}w`).join(', ') || undefined}
                              sizes="(max-width: 767px) 100vw, (max-width: 1279px) 33vw, 25vw"
                              alt={product.seo_image_alt || product.title}
                              width={600}
                              height={360}
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                          ) : (
                            <>
                              <div className="absolute inset-0 bg-gradient-to-br from-copper/10 to-copper-dark/10 group-hover:from-copper/20 group-hover:to-copper-dark/20 transition-all duration-300"></div>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-24 h-24 bg-copper/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                  <IconComponent className="w-12 h-12 text-copper" />
                                </div>
                              </div>
                            </>
                          )}

                          {/* Category Badge */}
                          {product.category_name && (
                            <div className="absolute top-3 right-3 px-3 py-1 bg-copper/90 text-white text-xs rounded-full">
                              {product.category_name}
                            </div>
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="p-6">
                          <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-copper transition-colors duration-300" style={{ fontSize: '15px' }}>
                            {product.title}
                          </h3>
                          <p className="text-muted-foreground mb-4 line-clamp-2">
                            {product.description}
                          </p>

                          {/* Gallery Preview */}
                          {product.images && product.images.length > 0 && (
                            <div className="flex space-x-2 space-x-reverse">
                              {product.images.slice(0, 3).map((image, imageIndex) => (
                                <div
                                  key={imageIndex}
                                  className="w-12 h-12 bg-muted rounded-lg overflow-hidden border border-border group-hover:border-copper/30 transition-all duration-300"
                                >
                                  <img
                                    src={responsiveApiAssetUrl(image.image_url, 160) || image.image_url}
                                    loading="lazy"
                                    decoding="async"
                                    alt={image.alt_text || `تصویر ${imageIndex + 1}`}
                                    width={160}
                                    height={160}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ))}
                              {product.images.length > 3 && (
                                <div className="w-12 h-12 bg-copper/10 rounded-lg flex items-center justify-center text-copper text-sm font-medium">
                                  +{product.images.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-r from-copper/0 via-copper/5 to-copper/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Products;
